import type { Priority, Status, TaskLevel, TaskType } from "../lib/api"
import { COLOR_DOT, COLOR_TEXT, priorityOf, statusOf, typeOf, useBoardConfig } from "../lib/boardConfig"

// Inline SVG, no icon package.
//
// Every glyph is 16×16 on a 16-unit viewBox, drawn in `currentColor` so it
// takes the colour of whatever it sits in — which is what keeps the palette in
// index.css the only place a colour is chosen. A dependency here would be ~40kB
// to draw the eleven shapes this app actually uses.

type IconProps = { className?: string }

const Svg = ({ className = "size-4", children }: IconProps & { children: React.ReactNode }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    {children}
  </svg>
)

export const ChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 6l4 4 4-4" />
  </Svg>
)

export const ChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 4l4 4-4 4" />
  </Svg>
)

export const ChevronLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 4L6 8l4 4" />
  </Svg>
)

export const Plus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 3.5v9M3.5 8h9" />
  </Svg>
)

export const Close = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4l8 8M12 4l-8 8" />
  </Svg>
)

export const Pencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10.5 3.5l2 2L6 12H4v-2l6.5-6.5z" />
  </Svg>
)

export const Trash = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.5 8.5h6l.5-8.5" />
  </Svg>
)

export const Search = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="7" cy="7" r="4" />
    <path d="M10 10l3 3" />
  </Svg>
)

export const More = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="8" cy="3.5" r=".75" fill="currentColor" />
    <circle cx="8" cy="8" r=".75" fill="currentColor" />
    <circle cx="8" cy="12.5" r=".75" fill="currentColor" />
  </Svg>
)

export const Clock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="8" cy="8" r="5.5" />
    <path d="M8 5v3.2l2 1.3" />
  </Svg>
)

export const Sun = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="8" cy="8" r="2.5" />
    <path d="M8 1.5v1.25M8 13.25v1.25M1.5 8h1.25M13.25 8h1.25M3.4 3.4l.9.9M11.7 11.7l.9.9M12.6 3.4l-.9.9M4.3 11.7l-.9.9" />
  </Svg>
)

export const Moon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12.8 10.3A5.5 5.5 0 015.7 3.2a5.5 5.5 0 107.1 7.1z" />
  </Svg>
)

export const Calendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
    <path d="M2.5 6.5h11M5.5 2v2M10.5 2v2" />
  </Svg>
)

export const Check = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 8.5l3 3 6-7" />
  </Svg>
)

export const Grip = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="6" cy="4" r=".9" fill="currentColor" stroke="none" />
    <circle cx="10" cy="4" r=".9" fill="currentColor" stroke="none" />
    <circle cx="6" cy="8" r=".9" fill="currentColor" stroke="none" />
    <circle cx="10" cy="8" r=".9" fill="currentColor" stroke="none" />
    <circle cx="6" cy="12" r=".9" fill="currentColor" stroke="none" />
    <circle cx="10" cy="12" r=".9" fill="currentColor" stroke="none" />
  </Svg>
)

export const Sort = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 3v10M2.5 10.5L5 13l2.5-2.5M11 13V3M8.5 5.5L11 3l2.5 2.5" />
  </Svg>
)

export const Board = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2" y="3" width="4" height="10" rx="1" />
    <rect x="8" y="3" width="4" height="6" rx="1" />
  </Svg>
)

export const List = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 4.5h10M3 8h10M3 11.5h6" />
  </Svg>
)

// ---------- task type ----------
//
// Three shapes, not three colours of the same shape: colour alone fails for the
// ~8% of men with a colour vision deficiency, and these sit at 14px where hue
// is hardest to judge.

const TYPE_GLYPH: Record<TaskType, React.ReactNode> = {
  feature: <path d="M8 2.5l1.6 3.4 3.7.5-2.7 2.6.7 3.7L8 10.9 4.7 12.7l.7-3.7-2.7-2.6 3.7-.5z" />,
  bug: (
    <>
      <rect x="5" y="5.5" width="6" height="7" rx="3" />
      <path d="M2.5 7.5h2.5M11 7.5h2.5M3 11h2M11 11h2M6 4l-1-1.5M10 4l1-1.5" />
    </>
  ),
  improvement: <path d="M3 11l3.5-3.5 2.5 2.5L13 5.5M13 5.5H9.5M13 5.5V9" />,
}

export const TYPE_LABELS: Record<TaskType, string> = {
  feature: "Feature",
  bug: "Bug",
  improvement: "Improvement",
}

export function TypeIcon({ type, className = "size-3.5" }: { type: TaskType; className?: string }) {
  const config = useBoardConfig()
  const meta = typeOf(config, type)
  // The three shipped types keep their glyphs; a custom type gets the neutral
  // arrow with its own colour and name — recognisable without a bespoke icon.
  return (
    <span title={meta.name} className={`inline-flex ${COLOR_TEXT[meta.color] ?? "text-faint"}`}>
      <Svg className={className}>{TYPE_GLYPH[type] ?? TYPE_GLYPH.improvement}</Svg>
      <span className="sr-only">{meta.name}</span>
    </span>
  )
}

// ---------- level ----------
//
// Where an item sits — epic, task or sub-task — as a short text badge rather
// than a glyph: the level is a structural fact people scan for, and three
// letters read faster than three new icons. Epic is the only one in colour, so
// the bodies of work stand out among the cards.

const LEVEL_BADGE: Record<TaskLevel, { short: string; name: string; className: string }> = {
  epic: { short: "EPIC", name: "Epic", className: "border-interactive/30 bg-interactive/10 text-interactive" },
  task: { short: "TASK", name: "Task", className: "border-border bg-surface-muted text-muted" },
  subtask: { short: "SUB", name: "Sub-task", className: "border-border bg-surface text-faint" },
}

export function LevelIcon({ level, className = "" }: { level: TaskLevel; className?: string }) {
  const meta = LEVEL_BADGE[level] ?? LEVEL_BADGE.task
  return (
    <span
      title={meta.name}
      className={`inline-flex shrink-0 items-center rounded border px-1 font-mono text-[9px] leading-4 font-semibold tracking-wide ${meta.className} ${className}`}
    >
      <span aria-hidden="true">{meta.short}</span>
      <span className="sr-only">{meta.name}</span>
    </span>
  )
}

// ---------- priority ----------
//
// Four ascending bars, the way Linear draws it: the height carries the value on
// its own, so the colour is reinforcement rather than the only signal. "none" is
// drawn as three flat dashes — visibly unset, not quietly zero. Those glyphs
// belong to the five built-in ids; a board's own priority (an imported
// "Show-stopper") is a dot in its colour, named by its title.

const PRIORITY_FILLED: Record<string, number> = { none: 0, low: 1, medium: 2, high: 3, urgent: 3 }

const PRIORITY_COLOUR: Record<string, string> = {
  none: "text-faint",
  low: "text-muted",
  medium: "text-muted",
  high: "text-warning",
  urgent: "text-danger",
}

export function PriorityIcon({
  priority,
  className = "size-3.5",
}: {
  priority: Priority
  className?: string
}) {
  const config = useBoardConfig()
  const meta = priorityOf(config, priority)
  const filled = PRIORITY_FILLED[priority]
  if (filled === undefined) {
    return (
      <span title={meta.name} className="inline-flex items-center justify-center">
        <span aria-hidden="true" className={`${className} inline-flex items-center justify-center`}>
          <span className={`size-2 rounded-full ${COLOR_DOT[meta.color] ?? "bg-muted"}`} />
        </span>
        <span className="sr-only">{meta.name}</span>
      </span>
    )
  }
  const bars = [
    { x: 2, y: 9.5, h: 4 },
    { x: 6.5, y: 7, h: 6.5 },
    { x: 11, y: 4.5, h: 9 },
  ]

  return (
    <span title={meta.name} className={`inline-flex ${PRIORITY_COLOUR[priority]}`}>
      <svg viewBox="0 0 16 16" aria-hidden="true" className={className}>
        {priority === "none" ? (
          bars.map((bar) => (
            <rect key={bar.x} x={bar.x} y="11.5" width="3" height="2" rx="1" fill="currentColor" opacity="0.45" />
          ))
        ) : (
          bars.map((bar, index) => (
            <rect
              key={bar.x}
              x={bar.x}
              y={bar.y}
              width="3"
              height={bar.h}
              rx="1"
              fill="currentColor"
              opacity={index < filled ? 1 : 0.25}
            />
          ))
        )}
        {/* Urgent is three full bars plus a mark, so it is not mistaken for
            "high" at a glance in a dense column. */}
        {priority === "urgent" && <circle cx="14" cy="2.5" r="2" fill="currentColor" />}
      </svg>
      <span className="sr-only">{meta.name}</span>
    </span>
  )
}

// ---------- status ----------
//
// A ring that fills as the work advances, which is what makes a column readable
// when it is scrolled past its header.

// The glyph says the *category* (empty ring → half disc → filled tick), the
// colour says the status. Category is fixed, so a board with invented statuses
// still reads at a glance; the colour comes from the admin's palette choice.
export function StatusIcon({ status, className = "size-3.5" }: { status: Status; className?: string }) {
  const config = useBoardConfig()
  const meta = statusOf(config, status)
  // The shipped board keeps its hand-tuned glyph nuances (review dot, blocked
  // slash); custom statuses fall back to their category's shape.
  const shape =
    status === "in_review" ? "review" :
    status === "blocked" ? "blocked" :
    meta.category

  return (
    <span className={`inline-flex ${COLOR_TEXT[meta.color] ?? "text-faint"}`} title={meta.name}>
      <svg viewBox="0 0 16 16" aria-hidden="true" className={className}>
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
        {shape === "in_progress" && (
          <path d="M8 8V3.2A4.8 4.8 0 018 12.8z" fill="currentColor" transform="rotate(0 8 8)" />
        )}
        {shape === "review" && <circle cx="8" cy="8" r="3" fill="currentColor" opacity="0.55" />}
        {shape === "done" && (
          <>
            <circle cx="8" cy="8" r="6" fill="currentColor" />
            <path
              d="M5.2 8.2l2 2 3.6-4"
              fill="none"
              stroke="var(--color-background)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
        {shape === "blocked" && <path d="M5.5 5.5l5 5" stroke="currentColor" strokeWidth="1.6" />}
      </svg>
    </span>
  )
}
